<template>
    <!-- Alert for starting clock -->
    <!-- <div v-if="canStartClock" class="alert alert-info d-flex align-items-center justify-content-between mb-3">
        <div>
            <Clock :size="18" class="me-2" />
            <strong>This assignment has not been started.</strong>
        </div>
        <button 
            type="button" 
            class="btn btn-sm btn-primary"
            @click="startClock"
        >
            <Play :size="16" class="me-1" />
            Start Clock
        </button>
    </div> -->

    <div class="row">
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Assigned To</span>
                    <tech-ac 
                        v-model="selectedTechId" 
                        placeholder="Select technicians..."
                        @change="onTechChange"
                    ></tech-ac>
                </label>
                
                <!-- Selected Technicians Badges -->
                <div v-if="selectedTechnicians.length > 0" class="d-flex flex-wrap gap-2 mt-2">
                    <span 
                        v-for="tech in selectedTechnicians" 
                        :key="tech.Id"
                        class="badge bg-primary d-flex align-items-center gap-2"
                        style="font-size: 0.85rem; padding: 0.3rem 0.5rem;"
                    >
                        {{ tech.TechnicianName }}
                        <button 
                            type="button" 
                            class="btn-close btn-close-white" 
                            style="font-size: 0.5rem;"
                            @click="removeTechnician(tech.Id)"
                            aria-label="Remove"
                        ></button>
                    </span>
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Client</span>
                    <client-ac 
                        v-model="form.CustomerNumber" 
                        inputclass="form-control form-control-sm" 
                        placeholder="Search for a client..."
                    ></client-ac>
                </label>
            </div>
        </div>
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Request/Job</span>
                    <job-ac 
                        v-model="form.RequestId" 
                        :client="form.CustomerNumber"
                        placeholder="Search for a request..."
                    ></job-ac>
                </label>
            </div>
        </div>
    </div>

    <div class="row">
        <div :class="form.Id ? 'col-md-4' : 'col-md-6'">
            <label class="d-block mb-3">
                <span>Estimated Time</span>
                <input 
                    class="form-control form-control-sm" 
                    type="text" 
                    v-model="form.EstimatedTime" 
                    placeholder="Hour Estimate" 
                />
            </label>
        </div>
        <div v-if="form.Id" class="col-md-4">
            <label class="d-block mb-3">
                <span>Status</span>
                <select 
                    class="form-select form-select-sm" 
                    v-model="form.Status"
                >
                    <option :value="undefined">Select Status...</option>
                    <option 
                        v-for="status in statusOptions" 
                        :key="status.value" 
                        :value="status.value"
                    >
                        {{ status.label }}
                    </option>
                </select>
            </label>
        </div>
        <div :class="form.Id ? 'col-md-4' : 'col-md-6'">
            <label class="d-block mb-3">
                <span>Start By</span>
                <div class="input-group input-group-sm">
                    <button 
                        class="btn btn-outline-secondary" 
                        type="button"
                        @click="adjustDate(-1)"
                        title="Previous day"
                    >
                        <i class="fa fa-chevron-left"></i>
                    </button>
                    <input type="datetime-local" 
                        class="form-control form-control-sm" 
                        v-model="form.CompleteByDate" 
                        placeholder="Complete By"
                    >
                    <button 
                        class="btn btn-outline-secondary" 
                        type="button"
                        @click="adjustDate(1)"
                        title="Next day"
                    >
                        <i class="fa fa-chevron-right"></i>
                    </button>
                </div>
            </label>
        </div>  
    </div>

    <div class="row">
        <div class="col-12">
            <label class="d-block mb-3">
                <span>Work Assignment</span>
                <textarea class="form-control form-control-sm" placeholder="Work Assignment" v-model="form.WorkAssignment"
                    rows="6"></textarea>
            </label>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch } from "vue";
import { Clock, Play } from "lucide-vue-next";
import TechAc from "@/components/Techs/TechAc.vue";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import ApiService from "@/services/ApiService";
import { useTechStore } from "@/stores/TechStore";
import { useTaskStatusStore } from "@/stores/TaskStatusStore";
import { storeToRefs } from "pinia";

const props = defineProps({
    assignedTechnicianIds: {
        type: Array as () => number[],
        default: () => []
    },
    ownerId: {
        type: Number,
        default: null
    }
});

const emit = defineEmits(['start-clock']);

const model = defineModel<any>();

const techStore = useTechStore();
const { list: TechList } = storeToRefs(techStore);

const taskStatusStore = useTaskStatusStore();
const { list: taskStatusList } = storeToRefs(taskStatusStore);

// Convert store list to options array for select dropdown
const statusOptions = computed(() => 
    Object.values(taskStatusList.value).map(status => ({
        value: status.Id,
        label: status.Status
    }))
);

// Load task statuses
taskStatusStore.load();

// Multiple technician selection
const selectedTechId = ref('');
const selectedTechnicians = ref<any[]>([]);

// Expose selected technicians to parent
defineExpose({
    selectedTechnicians
});

// Handle technician selection
const onTechChange = (techId: string) => {
    if (!techId) return;
    
    const tech = TechList.value[parseInt(techId)];
    if (!tech) return;
    
    // Check if already selected
    const alreadySelected = selectedTechnicians.value.some(t => t.Id === tech.Id);
    if (alreadySelected) {
        selectedTechId.value = '';
        return;
    }
    
    // Add to selection
    selectedTechnicians.value.push(tech);
    selectedTechId.value = '';
};

// Remove technician from selection
const removeTechnician = (techId: number) => {
    selectedTechnicians.value = selectedTechnicians.value.filter(t => t.Id !== techId);
};

// Adjust date by days
const adjustDate = (days: number) => {
    if (!form.value.CompleteByDate) {
        // If no date set, use today
        const today = new Date();
        form.value.CompleteByDate = today.toISOString().slice(0, 16);
    }
    
    const currentDate = new Date(form.value.CompleteByDate);
    currentDate.setDate(currentDate.getDate() + days);
    form.value.CompleteByDate = currentDate.toISOString().slice(0, 16);
};

// Build filtered list of technicians from assigned IDs and owner
const filteredTechnicians = computed(() => {
    // Combine owner and assigned technician IDs
    const techIds = new Set<number>();
    
    if (props.ownerId) {
        techIds.add(props.ownerId);
    }
    
    if (props.assignedTechnicianIds && props.assignedTechnicianIds.length > 0) {
        props.assignedTechnicianIds.forEach(id => techIds.add(id));
    }
    
    // If no specific technicians, return empty (will show full autocomplete)
    if (techIds.size === 0) {
        return [];
    }
    
    // Filter TechList to only include assigned technicians
    return Object.values(TechList.value).filter((tech: any) => techIds.has(tech.Id));
});

const form = computed({
    get: () => {
        if (model.value && model.value.Type !== 'assignment') {
            model.value.Type = 'assignment';
        }
        return model.value || {};
    },
    set: (value) => {
        if (value) {
            value.Type = 'assignment';
        }
        model.value = value;
    }
});

// Check if we can show the Start Clock button
const canStartClock = computed(() => {
    return model.value?.Id && !model.value?.Clock;
});

// Emit event to start clock
const startClock = () => {
    if (model.value?.Id) {
        emit('start-clock', model.value.Id);
    }
};
</script>
